<template>
<div>
  <!-- Add new firmware -->
  <el-column>
    <el-column-item>
      <el-inline :margin-top="false">
        <span><b>Version </b><i>({{convertVersionNumber(newFirmware.version)}})</i></span>
        <el-input
          v-model="newFirmware.version"
          placeholder="Write version in decimal form, e.g. 1.2"
          />
      </el-inline>
      <el-inline :margin-top="true">
        <b>Description</b>
        <el-input
          v-model="newFirmware.description"
          placeholder="What's new in this version?"
          />
      </el-inline>
    </el-column-item>
    <el-column-item>
      <b>Firmware</b>
      <el-file-drop
        accept=".zip"
        @select="handleFiles"
        />
      <i v-show="newFirmware.file !== null">
        File is selected
      </i>
    </el-column-item>
  </el-column>
  <el-button
    :margin-top="true"
    :disabled="disableUpload"
    @click="upload()"
    >

    Upload new Firmware
  </el-button>

  <el-loader v-if="isPending"/>
  <el-delimiter />

  <!-- All firmwares -->
  <div
    v-for="firmware in headQFirmware"
    :key="firmware.id"
    @click="open(firmware.id)"
    class="firmware"
    >
    <el-inline :space-between="true">
      <span>Version: <b>{{getVersionNumber(firmware.version)}}</b></span>
      <el-tag
        :success="firmware.status === 1"
        :warning="firmware.status !== 1"
        >
        {{status(firmware.status)}}
      </el-tag>
    </el-inline>
    <el-inline :space-between="true">
      <span>Description: <b>{{firmware.description}}</b></span>
      <i>{{(new Date(firmware.timestamp).toDateString())}}</i>
    </el-inline>
    <el-inline
      v-if="firmware.profiles.length > 0"
      >
      <span>Profiles: </span>
      <b
        v-for="(profile, index) in firmware.profiles"
        :key="`${firmware.id}_${profile}`"
        style="margin-left: 5px;"
        >
        {{profile}}
        <span v-if="index !== firmware.profiles.length - 1">,</span>
      </b>
    </el-inline>
    <el-delimiter :short="false" />
  </div>

  <!-- Edit profiles to a firmware -->
  <el-modal root
    v-if="editId !== ''"
    @close="editId = ''"
    >
    <h3>Edit firmware status</h3>
    <p>
      <i>{{editFirmware.description}}</i><br>
    </p>

    <el-inline :margin-top="true">
      <el-dropdown
        :options="profiles"
        v-model="editFirmwareProfile"
        :margin-right="true"
        style="flex-grow: 1; display: inline-flex;"
        />
      <el-button
        @click="confirmFirmwareChange = true"
        :disabled="editProfileExists"
        :tooltip="editProfileExists ? 'Firmware already exists for this profile' : 'NOTE! This will overwrite another firmware for this profile'"
        :margin-right="true"
        >
        Add profile to firmware
      </el-button>
      <el-button
        @click="download(editFirmware.downloadUrl)"
        :margin-right="true"
        :secondary="true"
        >
        Download
      </el-button>
    </el-inline>

    <!-- All profiles connected to this firmware -->
    <el-inline
      :margin-top="true"
      >
      <p>Current profiles: </p>
      <b
        v-for="(profile, index) in editFirmware.profiles"
        :key="profile"
        style="margin-left: 5px;"
        >
        {{profile}}
        <span v-if="index !== editFirmware.profiles.length - 1">,</span>
      </b>
    </el-inline>
  </el-modal>

  <!-- Confirm change -->
  <el-modal root
    v-if="confirmFirmwareChange"
    @close="confirmFirmwareChange = false"
    >
    <h1>NOTE!</h1>
    <p>Are you sure you want to change the firmware version for <b>{{editFirmwareProfile}}</b>?</p>
    <el-button
      @click="activate(editId, editFirmwareProfile)"
      :danger="true"
      >
      Yes, I'm sure
    </el-button>

    <el-loader v-if="pendingFirmwareChange" />
  </el-modal>
</div>
</template>
<script>
import logScope from 'log-scope'
import ElFileDrop from 'components/elements/ElFileDrop.vue'
import converter from 'script/converter.js'
import download from 'services/download.js'

import storage from 'services/storage.js'
import firestore from 'services/firestore.js'

const log = logScope('HeadQFirmware')

export default {
  components: { ElFileDrop },
  data () {
    return {
      confirmFirmwareChange: false,
      pendingFirmwareChange: false,
      isPending: false,
      editId: '',
      newFirmware: {
        version: '',
        description: '',
        file: null,
        status: 0
      },
      editFirmwareProfile: null
    }
  },
  computed: {
    disableUpload () {
      return this.newFirmware.version === '' ||
        this.newFirmware.description === '' ||
        this.newFirmware.file === null
    },
    headQFirmware () {
      return this.$store.getters.headQFirmware
    },
    editFirmware () {
      if (!this.headQFirmware) {
        return {
          description: '',
          profiles: []
        }
      }

      return this.headQFirmware.find(f => f.id === this.editId)
    },
    profiles () {
      const profiles = this.$store.getters.headQ

      if (!profiles) {
        return [{
          value: null,
          label: '-- Profile --'
        }]
      }

      return [{
        value: null,
        label: '-- Profile --'
      },
      ...profiles
        .map(profile => ({
          value: profile.profile,
          label: profile.profile
        }))
        .filter(profile => this.editFirmware.profiles.indexOf(profile.value) === -1)
      ]
    },
    editProfileExists () {
      return this.editFirmware.profiles.indexOf(this.editFirmwareProfile) !== -1
    }
  },
  methods: {
    open (id) {
      this.editId = id
      this.editFirmwareProfile = null

      log.debug('Edit firmware', this.editFirmware)
    },
    handleFiles (files) {
      log.debug('Upload files', files)

      if (files.length === 0) {
        return
      }

      this.newFirmware.file = files[0]
    },
    async upload () {
      const version = this.convertVersionNumber(this.newFirmware.version)
      if (isNaN(version) || !version || version < 250) {
        alert('Invalid version number')
        return
      }

      this.isPending = true

      try {
        const file = this.newFirmware.file
        const uploadedData = await storage.upload(file)
        const data = {
          downloadUrl: uploadedData.downloadURL,
          filename: uploadedData.filename,
          timestamp: +(new Date()),
          description: this.newFirmware.description,
          status: 0,
          profiles: [],
          version
        }
        await firestore.add('headQFirmware', data)

        log.success('Uploaded new firmware', data, uploadedData)
      } catch (err) {
        log.error('Could not upload file', err)
      }

      this.isPending = false

      this.newFirmware = {
        version: '',
        description: '',
        file: null
      }
    },
    getVersionNumber (hexVersion) {
      return converter.hexToBit(Number(hexVersion)).reverse().join('.')
    },
    convertVersionNumber (decVersion) {
      // e.g. str = 1.2
      const word = decVersion.replace(/,/, '.')
        .split('.')
        .map(bit => Number(bit))
        .reverse()

      if (word.length !== 2) {
        return 0
      }

      const hex = converter.wordToHex(word)
      return Number(hex)
    },
    status (status) {
      return {
        1: 'Active',
        0: 'Deprecated'
      }[status] || 'Deprecated'
    },
    async activate (id, profile) {
      const profiles = [ ...this.editFirmware.profiles, profile ]
      const version = this.editFirmware.version

      log.debug('New profiles', profiles)
      this.pendingFirmwareChange = true

      try {
        await firestore.set('headQFirmware', id, {
          status: 1,
          profiles
        }, {
          merge: true
        })

        await this.deactivate(id, profile)
        await this.updateProfileVersionNumber(version, profile)

        log.success(`Activated ${profile}, ID: ${id}`)
      } catch (err) {
        log.error('Could not activate: ' + id, err)
      }

      this.pendingFirmwareChange = false
      this.confirmFirmwareChange = false
      this.editId = ''
    },
    async deactivate (keepId, profile) {
      const snapshots = await firestore.db
        .collection('headQFirmware')
        .where('profiles', 'array-contains', profile)
        .get()

      log.debug('snapshots', snapshots)

      await Promise.all(snapshots.docs
        .filter(snapshot => snapshot.id !== keepId)
        .map(async snapshot => {
          const id = snapshot.id
          const data = snapshot.data()
          const profiles = data.profiles
            .filter(currentProfile => currentProfile !== profile)
          let status = data.status

          // Clear
          if (status !== 0 && profiles.length === 0) {
            status = 0
          }

          await firestore.set('headQFirmware', id, {
            profiles,
            status
          }, {
            merge: true
          })

          log.success('Updated Firmware ID: ' + id)
        })
      )

      log.success('Disable all profiles: ' + profile)
    },
    async updateProfileVersionNumber (firmwareVersion, profile) {
      const snapshots = await firestore.db
        .collection('headQ')
        .where('profile', '==', profile)
        .get()

      await Promise.all(snapshots.docs
        .map(async snapshot => {
          const id = snapshot.id

          await firestore.set('headQ', id, {
            firmwareVersion
          }, {
            merge: true
          })

          log.success(`Updated version number (${firmwareVersion}) on profile (${profile}). ID: ${id}`)
        }))
    },
    download (url) {
      download(url)
    }
  }
}
</script>
<style lang="less" scoped>
  .firmware {
    margin-bottom: 0px;
    cursor: pointer;
  }
</style>
